import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Download, CheckCircle, PenTool } from 'lucide-react';
import { Button } from '../components/ui/button';
import SignatureModal from '../components/SignatureModal';

const PartnerCaseDetailNewPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('auftragsdaten');
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gateStatus, setGateStatus] = useState(null);
  
  // Fragebogen State
  const [fragebogenData, setFragebogenData] = useState({
    victim_street: '',
    victim_city: '',
    victim_zip: '',
    victim_email: '',
    iban: '',
    vehicle_purchase_date: '',
    vehicle_first_registration: '',
    vorsteuerabzug: 'Nein',
    accident_time: '',
    accident_description: '',
    police_involved: false,
    witnesses: '',
    lawyer_desired: false,
    rental_car_needed: false,
    notes: ''
  });
  
  // Signature Modal State
  const [signatureModal, setSignatureModal] = useState({
    isOpen: false,
    documentId: null,
    documentType: null
  });

  useEffect(() => {
    const token = localStorage.getItem('partner_token');
    if (!token) {
      navigate('/partner');
      return;
    }
    loadCaseData();
    loadDocuments();
    loadGateStatus();
  }, [caseId]);

  const loadCaseData = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCaseData(data);
      
      // Pre-fill fragebogen if data exists
      setFragebogenData(prev => ({
        ...prev,
        victim_street: data.victim_street || '',
        victim_city: data.victim_city || '',
        victim_zip: data.victim_zip || '',
        victim_email: data.victim_email || '',
        iban: data.iban || '',
        vehicle_purchase_date: data.vehicle_purchase_date || '',
        vorsteuerabzug: data.vorsteuerabzug || 'Nein',
        accident_time: data.accident_time || '',
        accident_description: data.accident_description || '',
        police_involved: data.police_involved || false,
        witnesses: data.witnesses || '',
        lawyer_desired: data.lawyer_desired || false,
        rental_car_needed: data.rental_car_needed || false,
        notes: data.notes || ''
      }));
    } catch (error) {
      console.error('Load case failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/case/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Load documents failed:', error);
    }
  };

  const loadGateStatus = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/gate/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setGateStatus(data);
    } catch (error) {
      console.error('Load gate status failed:', error);
    }
  };

  const handleSaveFragebogen = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/fragebogen`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          case_id: caseId,
          ...fragebogenData
        })
      });
      alert('Fragebogen gespeichert');
      loadCaseData();
    } catch (error) {
      alert('Fehler beim Speichern');
    }
  };

  const handleCreateDocument = async (docType) => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          case_id: caseId,
          doc_type: docType
        })
      });
      const data = await response.json();
      
      if (data.success) {
        alert('Dokument erstellt');
        loadDocuments();
        loadGateStatus();
        // Open signature modal
        setSignatureModal({
          isOpen: true,
          documentId: data.document_id,
          documentType: docType
        });
      }
    } catch (error) {
      alert('Fehler beim Erstellen des Dokuments');
    }
  };

  const handleSignDocument = async (signatureData) => {
    try {
      const token = localStorage.getItem('partner_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/sign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_id: signatureModal.documentId,
          signature_data: signatureData
        })
      });
      
      alert('Dokument unterschrieben');
      setSignatureModal({ isOpen: false, documentId: null, documentType: null });
      loadDocuments();
      loadGateStatus();
    } catch (error) {
      alert('Fehler beim Unterschreiben');
    }
  };

  const getDocumentTypeName = (type) => {
    const names = {
      abtretung: 'Abtretungserklärung',
      vollmacht: 'Vollmacht Anwalt',
      rku: 'RKÜ-Bestätigung',
      fragebogen: 'Anspruchsteller-Fragebogen'
    };
    return names[type] || type;
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Lädt...</div>;
  }

  if (!caseData) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Fall nicht gefunden</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => navigate('/portal/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Zurück zum Dashboard
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              Akte: {caseData.license_plate || 'N/A'}
            </h1>
            <div className="w-32" /> {/* Spacer */}
          </div>

          {/* Tabs */}
          <div className="flex space-x-8 border-t pt-4 overflow-x-auto">
            {['auftragsdaten', 'fahrzeug', 'fragebogen', 'dokumente', 'übersicht'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-brand-blue text-brand-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'auftragsdaten' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-6">Auftragsdaten</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kennzeichen</label>
                <div className="text-gray-900">{caseData.license_plate || '-'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schadendatum</label>
                <div className="text-gray-900">{caseData.damage_date || '-'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schadenort</label>
                <div className="text-gray-900">{caseData.damage_location || '-'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Anspruchsteller</label>
                <div className="text-gray-900">{caseData.victim_name || '-'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                <div className="text-gray-900">{caseData.victim_phone || '-'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Versicherung</label>
                <div className="text-gray-900">{caseData.insurance_number || '-'}</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'fahrzeug' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-6">Fahrzeugdaten</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hersteller</label>
                <div className="text-gray-900">{caseData.vehicle_manufacturer || '-'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Modell</label>
                <div className="text-gray-900">{caseData.vehicle_model || '-'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kilometerstand</label>
                <div className="text-gray-900">{caseData.vehicle_mileage || '-'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schadenbeschreibung</label>
                <div className="text-gray-900">{caseData.damage_description || '-'}</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'fragebogen' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-6">Fragebogen</h2>
            
            {/* Allgemeine Daten */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Allgemeine Daten</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Straße, Hausnummer</label>
                  <input
                    type="text"
                    value={fragebogenData.victim_street}
                    onChange={(e) => setFragebogenData({...fragebogenData, victim_street: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PLZ</label>
                    <input
                      type="text"
                      value={fragebogenData.victim_zip}
                      onChange={(e) => setFragebogenData({...fragebogenData, victim_zip: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ort</label>
                    <input
                      type="text"
                      value={fragebogenData.victim_city}
                      onChange={(e) => setFragebogenData({...fragebogenData, victim_city: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                  <input
                    type="email"
                    value={fragebogenData.victim_email}
                    onChange={(e) => setFragebogenData({...fragebogenData, victim_email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
                  <input
                    type="text"
                    value={fragebogenData.iban}
                    onChange={(e) => setFragebogenData({...fragebogenData, iban: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                </div>
              </div>
            </div>

            {/* Fahrzeugdaten */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Fahrzeugdaten</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kaufdatum</label>
                  <input
                    type="date"
                    value={fragebogenData.vehicle_purchase_date}
                    onChange={(e) => setFragebogenData({...fragebogenData, vehicle_purchase_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vorsteuerabzugsberechtigt</label>
                  <select
                    value={fragebogenData.vorsteuerabzug}
                    onChange={(e) => setFragebogenData({...fragebogenData, vorsteuerabzug: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  >
                    <option value="Nein">Nein</option>
                    <option value="Ja">Ja</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Unfalldaten */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Unfalldaten</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Uhrzeit</label>
                  <input
                    type="time"
                    value={fragebogenData.accident_time}
                    onChange={(e) => setFragebogenData({...fragebogenData, accident_time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Polizei informiert</label>
                  <select
                    value={fragebogenData.police_involved ? 'Ja' : 'Nein'}
                    onChange={(e) => setFragebogenData({...fragebogenData, police_involved: e.target.value === 'Ja'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  >
                    <option value="Nein">Nein</option>
                    <option value="Ja">Ja</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unfallhergang</label>
                  <textarea
                    value={fragebogenData.accident_description}
                    onChange={(e) => setFragebogenData({...fragebogenData, accident_description: e.target.value})}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zeugen (Name, Kontakt)</label>
                  <input
                    type="text"
                    value={fragebogenData.witnesses}
                    onChange={(e) => setFragebogenData({...fragebogenData, witnesses: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                </div>
              </div>
            </div>

            {/* Abwicklung */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Abwicklung</h3>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={fragebogenData.lawyer_desired}
                    onChange={(e) => setFragebogenData({...fragebogenData, lawyer_desired: e.target.checked})}
                    className="h-4 w-4 text-brand-blue focus:ring-brand-blue border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Anwalt gewünscht</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={fragebogenData.rental_car_needed}
                    onChange={(e) => setFragebogenData({...fragebogenData, rental_car_needed: e.target.checked})}
                    className="h-4 w-4 text-brand-blue focus:ring-brand-blue border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Mietwagen benötigt</span>
                </label>
              </div>
            </div>

            {/* Anmerkungen */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Anmerkungen</h3>
              <textarea
                value={fragebogenData.notes}
                onChange={(e) => setFragebogenData({...fragebogenData, notes: e.target.value})}
                rows={4}
                placeholder="Weitere Anmerkungen..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSaveFragebogen}
                className="bg-brand-blue hover:bg-brand-blue/90"
              >
                Fragebogen speichern
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'dokumente' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-6">Dokumente</h2>
            
            {/* M11.4: Document Gate Status Box */}
            {gateStatus && !gateStatus.documents_ready_for_processing && (
              <div className="mb-6 p-4 border-2 border-orange-500 bg-orange-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-orange-900 mb-2">Pflichtdokumente fehlen</h3>
                    <p className="text-sm text-orange-800 mb-3">
                      Damit wir mit der Bearbeitung starten können, müssen folgende Dokumente unterschrieben werden:
                    </p>
                    <div className="space-y-2">
                      {gateStatus.missing_required_docs?.includes('abtretung') && (
                        <div className="flex items-center justify-between bg-white p-3 rounded border border-orange-200">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">Abtretungserklärung</span>
                            <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">Pflicht</span>
                          </div>
                          {documents.find(d => d.doc_type === 'abtretung' && !d.signed) ? (
                            <Button
                              size="sm"
                              onClick={() => {
                                const doc = documents.find(d => d.doc_type === 'abtretung' && !d.signed);
                                setSignatureModal({
                                  isOpen: true,
                                  documentId: doc.id,
                                  documentType: 'abtretung'
                                });
                              }}
                              className="bg-orange-600 hover:bg-orange-700 text-white"
                            >
                              Jetzt unterschreiben
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleCreateDocument('abtretung')}
                              className="bg-orange-600 hover:bg-orange-700 text-white"
                            >
                              Abtretung erstellen
                            </Button>
                          )}
                        </div>
                      )}
                      
                      {gateStatus.missing_required_docs?.includes('vollmacht') && (
                        <div className="flex items-center justify-between bg-white p-3 rounded border border-orange-200">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">Vollmacht Anwalt</span>
                            <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">Pflicht</span>
                          </div>
                          {documents.find(d => d.doc_type === 'vollmacht' && !d.signed) ? (
                            <Button
                              size="sm"
                              onClick={() => {
                                const doc = documents.find(d => d.doc_type === 'vollmacht' && !d.signed);
                                setSignatureModal({
                                  isOpen: true,
                                  documentId: doc.id,
                                  documentType: 'vollmacht'
                                });
                              }}
                              className="bg-orange-600 hover:bg-orange-700 text-white"
                            >
                              Jetzt unterschreiben
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleCreateDocument('vollmacht')}
                              className="bg-orange-600 hover:bg-orange-700 text-white"
                            >
                              Vollmacht erstellen
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Success message when ready */}
            {gateStatus && gateStatus.documents_ready_for_processing && (
              <div className="mb-6 p-4 border-2 border-green-500 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <div>
                    <h3 className="font-semibold text-green-900">Alle Pflichtdokumente vorhanden</h3>
                    <p className="text-sm text-green-700">Der Fall kann bearbeitet werden.</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Create Document Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <button
                onClick={() => handleCreateDocument('abtretung')}
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-brand-blue hover:bg-blue-50 transition-colors text-left"
              >
                <FileText className="h-8 w-8 text-brand-blue mb-2" />
                <h3 className="font-semibold text-gray-900">Abtretungserklärung</h3>
                <p className="text-sm text-gray-600">Mit Branding erstellen</p>
              </button>
              
              <button
                onClick={() => handleCreateDocument('vollmacht')}
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-brand-blue hover:bg-blue-50 transition-colors text-left"
              >
                <FileText className="h-8 w-8 text-gray-600 mb-2" />
                <h3 className="font-semibold text-gray-900">Vollmacht Anwalt</h3>
                <p className="text-sm text-gray-600">Neutral erstellen</p>
              </button>
              
              <button
                onClick={() => handleCreateDocument('rku')}
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-brand-blue hover:bg-blue-50 transition-colors text-left"
              >
                <FileText className="h-8 w-8 text-gray-600 mb-2" />
                <h3 className="font-semibold text-gray-900">RKÜ-Bestätigung</h3>
                <p className="text-sm text-gray-600">Neutral erstellen</p>
              </button>
              
              <button
                onClick={() => handleCreateDocument('fragebogen')}
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-brand-blue hover:bg-blue-50 transition-colors text-left"
              >
                <FileText className="h-8 w-8 text-gray-600 mb-2" />
                <h3 className="font-semibold text-gray-900">Anspruchsteller-Fragebogen</h3>
                <p className="text-sm text-gray-600">Neutral erstellen</p>
              </button>
            </div>

            {/* Document List */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Erstellte Dokumente</h3>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p>Keine Dokumente vorhanden</p>
                  <p className="text-sm">Erstellen Sie Dokumente mit den Buttons oben</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="h-6 w-6 text-brand-blue" />
                        <div>
                          <h4 className="font-medium text-gray-900">{getDocumentTypeName(doc.doc_type)}</h4>
                          <p className="text-sm text-gray-500">
                            {doc.signed ? (
                              <span className="flex items-center text-green-600">
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Unterschrieben
                              </span>
                            ) : (
                              <span className="text-orange-600">Noch nicht unterschrieben</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {!doc.signed && (
                          <Button
                            onClick={() => setSignatureModal({
                              isOpen: true,
                              documentId: doc.id,
                              documentType: doc.doc_type
                            })}
                            size="sm"
                            variant="outline"
                            className="flex items-center"
                          >
                            <PenTool className="h-4 w-4 mr-1" />
                            Unterschreiben
                          </Button>
                        )}
                        {doc.pdf_path && (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/download/${doc.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button size="sm" variant="outline">
                              <Download className="h-4 w-4 mr-1" />
                              Download
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'übersicht' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-6">Übersicht</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-brand-blue pl-4">
                <h3 className="font-semibold text-gray-900">Fall-ID</h3>
                <p className="text-gray-600">{caseData.id}</p>
              </div>
              <div className="border-l-4 border-brand-blue pl-4">
                <h3 className="font-semibold text-gray-900">Status</h3>
                <p className="text-gray-600">{caseData.status}</p>
              </div>
              <div className="border-l-4 border-brand-blue pl-4">
                <h3 className="font-semibold text-gray-900">Dokumente</h3>
                <p className="text-gray-600">{documents.length} Dokument(e) erstellt</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Signature Modal */}
      <SignatureModal
        isOpen={signatureModal.isOpen}
        onClose={() => setSignatureModal({ isOpen: false, documentId: null, documentType: null })}
        onSave={handleSignDocument}
        documentType={signatureModal.documentType}
      />
    </div>
  );
};

export default PartnerCaseDetailNewPage;